import {
  documentShell,
  esc,
  fieldList,
  isoDateToLong,
  num,
  table,
} from "./render.js";
import {
  IncompleteDocumentError,
  type DocumentHeader,
  type DocumentTemplate,
  type RenderedDocument,
} from "./types.js";

/**
 * The PAS 2030:2023 pre-installation building inspection (PIBI).
 *
 * Under PAS 2030:2023 §7.2.1 the Retrofit Installer inspects the property
 * before any measure is installed, and §7.2.6 requires the record and its
 * findings to be retained and made available to the Retrofit Coordinator or a
 * validation body on request. Every measure needs one; the 2023 edition moved
 * the requirement down to the measure level rather than the lodgement level.
 *
 * ONE TEMPLATE, TEN MEASURES. The measures differ in what the inspector must
 * look at, not in the shape of the record — the same inspector, competence
 * evidence, design review, client confirmation, findings and declaration
 * surround every one of them. So the measure-specific parts live in
 * `PAS_MEASURES` as data and the document is assembled from them. Ten
 * near-identical templates would drift apart inside a month, and the drift
 * would be invisible until an assessor found it.
 *
 * PROVENANCE IS PRINTED, NOT ASSUMED. Every checklist line carries a `basis`:
 * either a clause of PAS 2030:2023 / PAS 2035:2023 that it paraphrases, or an
 * explicit statement that it is our own practice. Both are printed against the
 * line. An authoritative-looking checklist whose provenance is unknowable is
 * worse than an obviously partial one, because somebody stands on it at audit.
 * The lines here were transcribed from the September 2023 editions of
 * PAS 2030:2023 and PAS 2035:2023 published by BSI; see PROVENANCE_NOTE below
 * for what still has to be checked against a controlled copy.
 *
 * RENDERS WHILE INCOMPLETE. This is filled in standing in a loft, frequently
 * with no signal. A half-finished record the inspector can see beats a refusal.
 * The completeness gate belongs at the point of ISSUING —
 * `missingPasInspectionItems` and `assertPasInspectionComplete` — exactly as
 * the commissioning checklist does it.
 *
 * VENTILATION IS NOT OPTIONAL ON AN INSULATION MEASURE. PAS 2030:2023 §5.2.4
 * requires the installer to check that any design containing a measure capable
 * of reducing background infiltration says how adequate ventilation will be
 * maintained, and PAS 2035:2023 §8.2.13 and Annex C.2.1 set the trigger:
 * Approved Document F category B or C, or any evidence of condensation or
 * mould. Insufficient ventilation after insulation is the single most common
 * cause of retrofit failure, complaint and audit non-conformity, so an
 * insulation inspection here cannot be issued until the assessment is made, and
 * the document says on its face when an intervention is required.
 */

// ---------------------------------------------------------------------------
// Provenance of the checklist data
// ---------------------------------------------------------------------------

/**
 * Printed on every rendered document.
 *
 * BSI publishes PAS 2030:2023 and PAS 2035:2023 without charge (they were
 * commissioned by government), which is why the clause references below are
 * transcriptions rather than recollections. They are still paraphrases written
 * to fit a form, not the standard's own words, and the copies used were the
 * September 2023 first printings — PAS 2035:2023 has since had a Corrigendum
 * No. 1 that has not been read into this file.
 */
export const PROVENANCE_NOTE =
  "Checklist items are paraphrased from PAS 2030:2023 and PAS 2035:2023 (BSI, September 2023). " +
  "The clause reference against each item is where it comes from; items marked as our practice are " +
  "not requirements of either standard. Verify against a controlled copy of the current edition, " +
  "including any corrigendum, before relying on this at audit.";

/**
 * Where a checklist line comes from.
 *
 * `transcribed` means it paraphrases a clause we read. `judged` means we put it
 * there for a reason of our own, and that reason gets printed too.
 */
export type CheckBasis =
  | {
      readonly kind: "transcribed";
      /** e.g. "PAS 2030:2023" */
      readonly standard: string;
      /** e.g. "B9-l2 1)" — the clause, exactly as the standard numbers it. */
      readonly clause: string;
      /**
       * True where the clause is a NOTE or otherwise says "should" rather than
       * "shall". Guidance and requirement are not the same thing at audit.
       */
      readonly guidanceOnly?: boolean;
    }
  | {
      readonly kind: "judged";
      /** Why we ask for it, printed on the page beside the item. */
      readonly why: string;
    };

export interface InspectionCheck {
  /** Stable identifier. Responses reference it; never renumber a shipped one. */
  readonly id: string;
  /** Section heading this item is grouped under on the page. */
  readonly group: string;
  /** What the inspector is confirming, phrased so the answer is yes or no. */
  readonly prompt: string;
  readonly basis: CheckBasis;
}

const pas2030 = (clause: string, guidanceOnly = false): CheckBasis => ({
  kind: "transcribed",
  standard: "PAS 2030:2023",
  clause,
  guidanceOnly,
});

const pas2035 = (clause: string, guidanceOnly = false): CheckBasis => ({
  kind: "transcribed",
  standard: "PAS 2035:2023",
  clause,
  guidanceOnly,
});

const judged = (why: string): CheckBasis => ({ kind: "judged", why });

// ---------------------------------------------------------------------------
// Core checks — clause 7.2 and clause 5.2 apply whatever the measure is
// ---------------------------------------------------------------------------

const GROUP_DESIGN = "Retrofit design and method statement";
const GROUP_PROPERTY = "The property and the installation location";
const GROUP_MEASURE = "Measure-specific inspection";
const GROUP_CLIENT = "Confirmation with the client";

const CORE_CHECKS: readonly InspectionCheck[] = [
  {
    id: "core.design-complete",
    group: GROUP_DESIGN,
    prompt:
      "The retrofit design is complete and suitable for this dwelling; nothing is missing that could reasonably be expected to be included.",
    basis: pas2030("7.2.4, 5.2.1"),
  },
  {
    id: "core.design-assumptions",
    group: GROUP_DESIGN,
    prompt:
      "Nothing observed on site is contrary to the stated design assumptions, including assumptions about anything that could only be examined by opening up the construction.",
    basis: pas2030("7.2.4"),
  },
  {
    id: "core.design-implementable",
    group: GROUP_DESIGN,
    prompt:
      "Every aspect of the design and specification can be implemented at this location.",
    basis: pas2030("7.2.4"),
  },
  {
    id: "core.method-statement",
    group: GROUP_DESIGN,
    prompt:
      "The location-specific installation method statement is suitable and complete for the work to be done here.",
    basis: pas2030("7.2.3, 6.1"),
  },
  {
    id: "core.repairs-completed",
    group: GROUP_DESIGN,
    prompt:
      "The repair works identified by the pre-installation assessment for the design have been completed.",
    basis: pas2030("7.2.1"),
  },
  {
    id: "core.moisture-risk",
    group: GROUP_PROPERTY,
    prompt:
      "Potential moisture build-up arising from this installation has been considered, including at moisture-sensitive locations such as embedded timbers.",
    basis: pas2030("7.2.1, 5.2.3"),
  },
  {
    id: "core.fire-safety",
    group: GROUP_PROPERTY,
    prompt:
      "The fire safety of the dwelling has been taken into account and is not compromised by the proposed installation.",
    basis: pas2030("7.2.1"),
  },
  {
    id: "core.services",
    group: GROUP_PROPERTY,
    prompt:
      "The functionality and safety of installed services — gas, electricity, water, telecommunications — is not compromised, and any risk to their ongoing performance has been noted for the Retrofit Designer or service provider.",
    basis: pas2030("7.2.1, 7.2.6 NOTE 3"),
  },
  {
    id: "core.access-and-storage",
    group: GROUP_PROPERTY,
    prompt:
      "Site layout, access and materials storage allow the work to be executed as designed.",
    // 7.2.1 sets the depth: enough to confirm the measure can be safely and
    // effectively installed here. 7.2.5 is the client's separate agreement to
    // the same arrangements, and is asked below under client confirmation.
    basis: pas2030("7.2.1"),
  },
  {
    id: "core.photographic-record",
    group: GROUP_PROPERTY,
    prompt:
      "Photographs have been taken of each area inspected, including every pre-existing defect recorded below.",
    basis: judged(
      "PAS 2030 requires the inspection and its findings to be recorded (7.2.6) but does not specify photographs. A dated photograph is what settles a later dispute about whether damage pre-dated the installer.",
    ),
  },
  {
    id: "core.client-scope",
    group: GROUP_CLIENT,
    prompt:
      "The nature and extent of the specified installation is known to the client and matches their expectations.",
    basis: pas2030("7.2.5"),
  },
  {
    id: "core.client-access",
    group: GROUP_CLIENT,
    prompt:
      "Arrangements for site access and materials storage are adequate and have been agreed with the client.",
    basis: pas2030("7.2.5"),
  },
  {
    id: "core.client-hours",
    group: GROUP_CLIENT,
    prompt: "The intended days and hours of working are acceptable to the client.",
    basis: pas2030("7.2.5"),
  },
];

/**
 * Extra core checks for measures that add insulation or airtightness.
 *
 * Split out because §5.2.4 and Annex A.5 are written about measures with the
 * potential to reduce background infiltration — not about a boiler swap.
 */
/**
 * The design-side ventilation check, named because the completeness gate looks
 * for findings raised against it.
 */
export const VENTILATION_DESIGN_CHECK_ID = "fabric.ventilation-in-design";

const FABRIC_CORE_CHECKS: readonly InspectionCheck[] = [
  {
    id: VENTILATION_DESIGN_CHECK_ID,
    group: GROUP_DESIGN,
    prompt:
      "The retrofit design states how adequate ventilation is to be maintained or provided, to keep internal air quality good and minimise surface and interstitial condensation risk.",
    basis: pas2030("5.2.4"),
  },
  {
    id: "fabric.thermal-bridging-details",
    group: GROUP_DESIGN,
    prompt:
      "The design includes construction details eliminating thermal bypass and minimising thermal bridging at corners, junctions and the edges of insulation layers; where it does not, the Retrofit Coordinator has confirmed in writing that this was intentional.",
    basis: pas2030("A.5, 5.2.1"),
  },
  {
    id: "fabric.air-barrier",
    group: GROUP_DESIGN,
    prompt:
      "The design includes construction details for a continuous air barrier, or the Retrofit Coordinator has confirmed in writing that its omission was intentional.",
    basis: pas2030("A.5"),
  },
];

// ---------------------------------------------------------------------------
// Measure-specific checks
// ---------------------------------------------------------------------------

const CAVITY_WALL_CHECKS: readonly InspectionCheck[] = [
  {
    id: "cwi.building-regulations",
    group: GROUP_MEASURE,
    prompt:
      "The installation would not result in non-compliance with the Building Regulations — workmanship, materials, structural stability, fire safety or resistance to moisture.",
    basis: pas2030("B1-l2 1)"),
  },
  {
    id: "cwi.combustion-appliances",
    group: GROUP_MEASURE,
    prompt:
      "The installation would not result in unsafe operation of any combustion appliance.",
    basis: pas2030("B1-l2 1)"),
  },
  {
    id: "cwi.ventilation-ducts",
    group: GROUP_MEASURE,
    prompt:
      "The installation would not compromise the functionality of existing ventilation ducts or systems.",
    basis: pas2030("B1-l2 1)"),
  },
  {
    id: "cwi.existing-services",
    group: GROUP_MEASURE,
    prompt:
      "The installation would not compromise the functionality or safety of existing services (gas, electricity, water, telephone).",
    basis: pas2030("B1-l2 1)"),
  },
  {
    id: "cwi.supplier-requirements",
    group: GROUP_MEASURE,
    prompt:
      "The proposed installation complies with every requirement stated by the system supplier.",
    basis: pas2030("B1-l2 1)"),
  },
  {
    id: "cwi.structure-suitable",
    group: GROUP_MEASURE,
    prompt:
      "The type and condition of the dwelling structure is suitable for the works to commence.",
    basis: pas2030("B1-l2 1)"),
  },
  {
    id: "cwi.cavity-extent",
    group: GROUP_MEASURE,
    prompt: "The extent of the cavity to be filled has been determined and recorded.",
    basis: pas2030("B1-l2 1)"),
  },
  {
    id: "cwi.site-conditions",
    group: GROUP_MEASURE,
    prompt: "Site layout and conditions will not impair execution of the works.",
    basis: pas2030("B1-l2 1)"),
  },
  {
    id: "cwi.asbestos",
    group: GROUP_MEASURE,
    prompt:
      "Relevant checks have been undertaken to determine whether materials containing asbestos are present.",
    basis: pas2030("B1-l2 1)"),
  },
  {
    id: "cwi.ventilation-openings",
    group: GROUP_MEASURE,
    prompt:
      "Essential ventilation openings requiring sleeving or safeguarding before installation have been identified.",
    basis: pas2030("B1-l1"),
  },
  {
    id: "cwi.flue-positions",
    group: GROUP_MEASURE,
    prompt:
      "The position of every flue, whether or not in service, has been recorded together with the measures that will safeguard its proper functioning.",
    basis: pas2030("B1-l1"),
  },
  {
    id: "cwi.party-wall-notice",
    group: GROUP_MEASURE,
    prompt:
      "Where the installation includes party walls, any notice required under the Party Wall etc. Act 1996 has been issued, or the Retrofit Designer has confirmed that none is required.",
    basis: pas2030("B1-l1 NOTE 3", true),
  },
];

/**
 * External and internal wall insulation share almost all of their inspection
 * requirements — B4-l2 and B8-l2 are the same text with a different system
 * build-up. Built from one function so the pair cannot drift.
 */
function solidWallChecks(
  prefix: "ewi" | "iwi",
  clause: "B4-l2" | "B8-l2",
  systemComponents: string,
  moisturePrompt: string,
): readonly InspectionCheck[] {
  const measureName =
    prefix === "ewi" ? "external wall insulation" : "internal wall insulation";
  return [
    {
      id: `${prefix}.pre-design-assessment`,
      group: GROUP_MEASURE,
      prompt:
        "A full and detailed pre-design building assessment was undertaken by a competent person before the retrofit design was produced.",
      basis: pas2030(`${clause} 1) a)`),
    },
    {
      id: `${prefix}.design-thermal-calculations`,
      group: GROUP_MEASURE,
      prompt:
        "The retrofit design takes full account of the pre-design assessment and includes thermal performance calculations.",
      basis: pas2030(`${clause} 1) b)`),
    },
    {
      id: `${prefix}.design-moisture-risk-analysis`,
      group: GROUP_MEASURE,
      prompt: "The retrofit design includes a moisture risk analysis.",
      basis: pas2030(`${clause} 1) b)`),
    },
    {
      id: `${prefix}.design-ventilation-details`,
      group: GROUP_MEASURE,
      prompt:
        "The retrofit design states the ventilation requirements and carries the standard or bespoke drawing details for them.",
      basis: pas2030(`${clause} 1) b)`),
    },
    {
      id: `${prefix}.design-system-components`,
      group: GROUP_MEASURE,
      prompt: `The retrofit design specifies the main components of the system: ${systemComponents}.`,
      basis: pas2030(`${clause} 1) b)`),
    },
    {
      id: `${prefix}.design-interface-details`,
      group: GROUP_MEASURE,
      prompt:
        "The design gives details for the main interfaces — thermal bridging, meter boxes, reveals, roofline and joists, party walls, the base detail with particular reference to below damp-proof course, floor details, seals at windows and doors, seals to penetrations, light fittings, sockets, surface-mounted structures, ceilings, roof, and junctions with other finishes or measures.",
      basis: pas2030(`${clause} 1) b)`),
    },
    {
      id: `${prefix}.design-interstitial-moisture`,
      group: GROUP_MEASURE,
      prompt: moisturePrompt,
      basis: pas2030(`${clause} 1) b)`),
    },
    {
      id: `${prefix}.design-practical`,
      group: GROUP_MEASURE,
      prompt: `Installing to the retrofit design is practical and achievable given the chosen ${measureName} system, this dwelling's construction, the site conditions and the other measures planned for the property.`,
      basis: pas2030(`${clause} 1) b)`),
    },
    {
      id: `${prefix}.building-regulations`,
      group: GROUP_MEASURE,
      prompt:
        "The installation would not result in non-compliance with the Building Regulations — workmanship, materials, structural stability or fire safety.",
      basis: pas2030(`${clause} 2)`),
    },
    {
      id: `${prefix}.moisture-management`,
      group: GROUP_MEASURE,
      prompt:
        prefix === "ewi"
          ? "The installation provides resilience to moisture; where it does not, an alternative solution or detail has been selected and every contract document amended to suit."
          : "The installation manages moisture; where it does not, an alternative solution or detail has been selected and every contract document amended to suit.",
      basis: pas2030(`${clause} 2)`),
    },
    {
      id: `${prefix}.avoidable-thermal-bridging`,
      group: GROUP_MEASURE,
      prompt:
        "The installation would not result in avoidable thermal bridging, and details carry additional capacity to manage water within the system should surface or interstitial condensation occur.",
      basis: pas2030(`${clause} 2)`),
    },
    {
      id: `${prefix}.combustion-appliances`,
      group: GROUP_MEASURE,
      prompt:
        "The installation would not result in unsafe operation of combustion appliances; where no alternative safe detail is available, work must not progress in the area causing it.",
      basis: pas2030(`${clause} 2)`),
    },
    {
      id: `${prefix}.ventilation-ducts`,
      group: GROUP_MEASURE,
      prompt:
        "The installation would not compromise the functionality of existing ventilation ducts or systems; where no alternative safe detail is available, work must not progress in the area causing it.",
      basis: pas2030(`${clause} 2)`),
    },
    {
      id: `${prefix}.existing-services`,
      group: GROUP_MEASURE,
      prompt:
        "The installation would not compromise the functionality or safety of existing services (gas, electricity, water, telephone).",
      basis: pas2030(`${clause} 2)`),
    },
    {
      id: `${prefix}.supplier-and-design-compliance`,
      group: GROUP_MEASURE,
      prompt:
        "The proposed installation complies with every requirement of the measure supplier and of the retrofit design.",
      basis: pas2030(`${clause} 2)`),
    },
    {
      id: `${prefix}.substrate-condition`,
      group: GROUP_MEASURE,
      prompt:
        "The condition of the substrate is suitable for the works to commence; where any part of it is not, proposals for adaptation or additional preparation have been prepared.",
      basis: pas2030(prefix === "ewi" ? "B4-l2 2)" : "B8-l2 3)"),
    },
    {
      id: `${prefix}.non-compliance-referred`,
      group: GROUP_MEASURE,
      prompt:
        "Every instance of potential non-compliance found by this inspection has been documented and referred to the Retrofit Coordinator, and any design adjustment, adaptation or additional preparation confirmed acceptable in writing by the system supplier or the Retrofit Coordinator.",
      basis: pas2030(prefix === "ewi" ? "B4-l2 2)" : "B8-l2 4)"),
    },
    {
      id: `${prefix}.documents-amended`,
      group: GROUP_MEASURE,
      prompt:
        "The retrofit design documentation and method statement have been amended for any specified change, and this inspection record updated to evidence that the modified installation addresses everything the inspection found.",
      basis: pas2030(prefix === "ewi" ? "B4-l2 2)" : "B8-l2 5)"),
    },
    {
      id: `${prefix}.industry-checklist`,
      group: GROUP_MEASURE,
      prompt:
        prefix === "ewi"
          ? "The industry pre-installation building inspection checklist for external wall insulation (NIA / INCA / SWIGA) has been completed alongside this record."
          : "An industry pre-installation building inspection checklist for internal wall insulation has been completed alongside this record, where one applies to the system being installed.",
      basis:
        prefix === "ewi"
          ? pas2030("B4-l2 NOTE, 10.7.1", true)
          : judged(
              "B8-l2 carries no equivalent NOTE. We ask for it because the same system suppliers publish checklists for internal systems and an assessor will expect the supplier's own document.",
            ),
    },
  ];
}

const EXTERNAL_WALL_CHECKS = solidWallChecks(
  "ewi",
  "B4-l2",
  "fixing type and method, insulation type and thickness, joint details and specifications, the reinforcing coat and type of reinforcement, and the finish",
  "The interface details clearly demonstrate how the installation avoids interstitial moisture risk, particularly at moisture-sensitive locations such as embedded timber joist ends, as well as surface condensation.",
);

const INTERNAL_WALL_CHECKS = solidWallChecks(
  "iwi",
  "B8-l2",
  "fixing type and method, insulation type and thickness, the breather membrane, the vapour control layer where required, board type and jointing, and the finish",
  "The interface details clearly demonstrate how the installation avoids moisture risk, particularly at moisture-sensitive locations such as embedded timber joist ends.",
);

/**
 * Loft insulation, B9-l2.
 *
 * The roof-space condition items are reused by room-in-roof insulation, because
 * B12-l2 1) requires the inspection to follow every measure-specific annex
 * relevant to the insulation types being installed.
 */
const ROOF_SPACE_CONDITION_CHECKS: readonly InspectionCheck[] = [
  {
    id: "loft.pre-existing-damage",
    group: GROUP_MEASURE,
    prompt:
      "Pre-existing damage to the areas that installation operatives will pass through or work in has been identified and recorded.",
    basis: pas2030("B9-l2 1)"),
  },
  {
    id: "loft.extent",
    group: GROUP_MEASURE,
    prompt: "The extent of the area and the elements to be insulated has been determined.",
    basis: pas2030("B9-l2 1)"),
  },
  {
    id: "loft.asbestos",
    group: GROUP_MEASURE,
    prompt:
      "Relevant checks have been undertaken to determine whether materials containing asbestos are present.",
    basis: pas2030("B9-l2 1)"),
  },
  {
    id: "loft.roof-space-ventilation",
    group: GROUP_MEASURE,
    prompt: "Appropriate roof space ventilation arrangements exist.",
    basis: pas2030("B9-l2 1)"),
  },
  {
    id: "loft.pests-and-protected-species",
    group: GROUP_MEASURE,
    prompt:
      "The roof space is free from rodents, pests and protected species such as bats.",
    basis: pas2030("B9-l2 1)"),
  },
  {
    id: "loft.timbers",
    group: GROUP_MEASURE,
    prompt: "Timbers are free from rot and infestation.",
    basis: pas2030("B9-l2 1)"),
  },
  {
    id: "loft.ceiling-condition",
    group: GROUP_MEASURE,
    prompt: "The condition of the ceiling is sound.",
    basis: pas2030("B9-l2 1)"),
  },
  {
    id: "loft.metal-members",
    group: GROUP_MEASURE,
    prompt: "Metal structural roof members are free from visible signs of corrosion.",
    basis: pas2030("B9-l2 1)"),
  },
  {
    id: "loft.electrical-wiring",
    group: GROUP_MEASURE,
    prompt:
      "Electrical wiring is free from visible defects such as damaged cables, trailing cables or exposed conductors.",
    basis: pas2030("B9-l2 1)"),
  },
  {
    id: "loft.water-penetration",
    group: GROUP_MEASURE,
    prompt: "There are no visible signs of water penetration.",
    basis: pas2030("B9-l2 1)"),
  },
  {
    id: "loft.water-system-leaks",
    group: GROUP_MEASURE,
    prompt:
      "There are no visible signs of leakage from water system components — pipework, cisterns, tanks.",
    basis: pas2030("B9-l2 1)"),
  },
  {
    id: "loft.designer-requirements",
    group: GROUP_MEASURE,
    prompt:
      "The proposed installation complies with every requirement stated by the designer or specifier.",
    basis: pas2030("B9-l2 1)"),
  },
  {
    id: "loft.supply-extract-ducts",
    group: GROUP_MEASURE,
    prompt:
      "The proposed installation would not compromise the functionality of existing air supply or extract ventilation ducts or systems.",
    basis: pas2030("B9-l2 1)"),
  },
  {
    id: "loft.combustion-appliances",
    group: GROUP_MEASURE,
    prompt:
      "The proposed installation would not result in unsafe operation of a combustion appliance.",
    basis: pas2030("B9-l2 1)"),
  },
  {
    id: "loft.access",
    group: GROUP_MEASURE,
    prompt: "There is appropriate access to the property and to the roof space.",
    basis: pas2030("B9-l2 1)"),
  },
  {
    id: "loft.clearance",
    group: GROUP_MEASURE,
    prompt: "The roof space is free from stored items, boarding and similar obstruction.",
    basis: pas2030("B9-l2 1)"),
  },
  {
    id: "loft.building-regulations",
    group: GROUP_MEASURE,
    prompt:
      "The installation would not result in non-compliance with the Building Regulations — workmanship, materials, structural stability, fire safety or resistance to moisture.",
    basis: pas2030("B9-l2 1)"),
  },
];

const ROOM_IN_ROOF_CHECKS: readonly InspectionCheck[] = [
  ...ROOF_SPACE_CONDITION_CHECKS.map((check) => ({
    ...check,
    // Same items, reached through B12-l2 1) rather than directly. Recording the
    // route matters: an assessor checking B.12 needs to see why B.9's list is here.
    id: check.id.replace(/^loft\./, "riri."),
    basis: pas2030(`B12-l2 1) applying ${describeBasis(check.basis)}`),
  })),
  {
    id: "riri.void-ventilation",
    group: GROUP_MEASURE,
    prompt:
      "Ventilation to spaces within the roof void that are not part of the room-in-roof space has been checked and assessed, and adequate ventilation is provided and maintained.",
    basis: pas2030("B12-l2 1) a)"),
  },
  {
    id: "riri.ridge-thermal-bridging",
    group: GROUP_MEASURE,
    prompt:
      "Thermal bridging at the ridge and at other connections with the main structure either needs no further attention or has been referred to the Retrofit Coordinator.",
    basis: pas2030("B12-l2 1) b)"),
  },
  {
    id: "riri.industry-checklist",
    group: GROUP_MEASURE,
    prompt:
      "The industry room-in-roof insulation pre-installation building inspection checklist has been completed alongside this record.",
    basis: pas2030("B12-l2 NOTE 1, 10.7.2", true),
  },
];

const BOILER_CHECKS: readonly InspectionCheck[] = [
  {
    id: "boiler.fabric-condition",
    group: GROUP_MEASURE,
    prompt:
      "The condition of the building fabric is satisfactory in relation to the proposed work.",
    basis: pas2030("C1-l2"),
  },
  {
    id: "boiler.electrical-condition",
    group: GROUP_MEASURE,
    prompt:
      "The condition of the existing electrical installation is satisfactory in relation to the proposed work.",
    basis: pas2030("C1-l2"),
  },
  {
    id: "boiler.building-regulations",
    group: GROUP_MEASURE,
    prompt:
      "The installation would not result in non-compliance with the Building Regulations — workmanship, materials, structural stability, fire safety, resistance to moisture, heat-producing appliances, or conservation of fuel and power.",
    basis: pas2030("C1-l2"),
  },
  {
    id: "boiler.gas-safety-regulations",
    group: GROUP_MEASURE,
    prompt:
      "The installation would not result in non-compliance with the relevant gas safety regulations.",
    basis: pas2030("C1-l2"),
  },
  {
    id: "boiler.manufacturer-requirements",
    group: GROUP_MEASURE,
    prompt:
      "The proposed installation complies with every requirement stated by the boiler manufacturer.",
    basis: pas2030("C1-l2"),
  },
  {
    id: "boiler.plume-nuisance",
    group: GROUP_MEASURE,
    prompt: "The proposed flue termination would not create a plume nuisance.",
    basis: pas2030("C1-l2"),
  },
  {
    id: "boiler.condensate-disposal",
    group: GROUP_MEASURE,
    prompt:
      "Any special condensate disposal arrangements needed have been identified, including protection against freezing.",
    basis: pas2030("C1-l2"),
  },
  {
    id: "boiler.water-hardness",
    group: GROUP_MEASURE,
    prompt:
      "Whether the dwelling is in a hard water area (above 200 ppm) has been established.",
    basis: pas2030("C1-l2"),
  },
  {
    id: "boiler.water-conditioning",
    group: GROUP_MEASURE,
    prompt:
      "Whether water conditioning arrangements are required in relation to the water hardness has been determined.",
    basis: pas2030("C1-l2"),
  },
  {
    id: "boiler.asbestos",
    group: GROUP_MEASURE,
    prompt:
      "Relevant checks have been undertaken to determine whether materials containing asbestos are present.",
    basis: pas2030("C1-l2"),
  },
  {
    id: "boiler.combustion-ventilation",
    group: GROUP_MEASURE,
    prompt:
      "Ventilation provision for the appliance and its compartment has been assessed against BS 5440-2, and flueing arrangements against BS 5440-1.",
    basis: judged(
      "C1-l2 does not list it, so this is our placement, not a transcribed inspection requirement. C1-l1 d) and e) make BS 5440-1 and BS 5440-2 applicable to the installation, and combustion ventilation cannot be put right once the appliance is in.",
    ),
  },
  {
    id: "boiler.system-cleaning",
    group: GROUP_MEASURE,
    prompt:
      "The condition of the existing heating system has been assessed and the cleaning and water treatment required by BS 7593 identified.",
    basis: judged(
      "C1-l2 does not list it, so this is our placement. C1-l1 f) makes BS 7593 applicable to the installation, and the state of the existing system decides whether cleaning is a variation — cheaper to establish before the work is priced than after.",
    ),
  },
];

const HEATING_CONTROLS_CHECKS: readonly InspectionCheck[] = [
  {
    id: "controls.compatibility",
    group: GROUP_MEASURE,
    prompt:
      "The proposed control arrangement is compatible with any existing controls for heating, hot water, ventilation or air conditioning.",
    basis: pas2030("C5-l2"),
  },
  {
    id: "controls.building-regulations",
    group: GROUP_MEASURE,
    prompt:
      "The installation would not result in non-compliance with the Building Regulations.",
    basis: pas2030("C5-l2"),
  },
  {
    id: "controls.manufacturer-requirements",
    group: GROUP_MEASURE,
    prompt:
      "The proposed installation complies with every requirement stated by the heating controls manufacturer.",
    basis: pas2030("C5-l2"),
  },
  {
    id: "controls.asbestos",
    group: GROUP_MEASURE,
    prompt:
      "Relevant checks have been undertaken to determine whether materials containing asbestos are present.",
    basis: pas2030("C5-l2"),
  },
  {
    id: "controls.electrical-supply",
    group: GROUP_MEASURE,
    prompt:
      "The existing electrical supply and wiring centre arrangements are adequate for the proposed controls, and any electrical work is within the scope of an EAS-compliant operative.",
    basis: judged(
      "C5-l2 does not say it. Controls work is electrical work, and C5-l4 requires EAS compliance for the operative, so the inspection is where an inadequate supply should surface.",
    ),
  },
];

function describeBasis(basis: CheckBasis): string {
  return basis.kind === "transcribed" ? basis.clause : "our practice";
}

// ---------------------------------------------------------------------------
// The measures
// ---------------------------------------------------------------------------

export type PasMeasure =
  | "cavity-wall-insulation"
  | "external-wall-insulation"
  | "internal-wall-insulation"
  | "loft-insulation"
  | "room-in-roof-insulation"
  | "boiler-replacement"
  | "heating-controls";

export interface MeasureDefinition {
  readonly id: PasMeasure;
  readonly name: string;
  /** PAS 2030:2023 measure reference, e.g. "BFM.9". */
  readonly measureReference: string;
  /** PAS 2030:2023 annex clause, e.g. "B.9". */
  readonly annex: string;
  /**
   * True where the measure adds insulation or reduces background infiltration.
   * Drives the mandatory ventilation and condensation risk section.
   */
  readonly isInsulation: boolean;
  /**
   * The annexes for the insulation measures require the inspection itself to be
   * independently checked before installation starts, or a recognised industry
   * quality assurance pre-notification instead. Null where the annex is silent.
   */
  readonly independentCheckClause: string | null;
  readonly checks: readonly InspectionCheck[];
}

function defineMeasure(
  id: PasMeasure,
  name: string,
  measureReference: string,
  annex: string,
  isInsulation: boolean,
  independentCheckClause: string | null,
  specific: readonly InspectionCheck[],
): MeasureDefinition {
  return {
    id,
    name,
    measureReference,
    annex,
    isInsulation,
    independentCheckClause,
    checks: [
      ...CORE_CHECKS,
      ...(isInsulation ? FABRIC_CORE_CHECKS : []),
      ...specific,
    ],
  };
}

/**
 * The measures this template covers.
 *
 * PAS 2030:2023 Annex A lists thirteen building fabric measures and ten
 * building services mechanical ones. The seven here are the ones the business
 * needs first; adding another is a new entry in this array, not a new file.
 * Not yet covered: draught proofing (B.2), glazing and doors (B.3), flat roof
 * (B.5), floor (B.6), hybrid wall (B.7), pitched roof (B.10), solar shading
 * (B.11), park homes (B.13), oil boilers (C.2), flue gas heat recovery (C.3),
 * heating system insulation (C.4), hot water systems (C.6), MVHR (C.7),
 * underfloor heating (C.8), warm-air heating (C.9).
 *
 * NOTE FOR WHOEVER WIRES THIS UP: a separate `packages/pas` measures package is
 * being built. These definitions were written here because that package does
 * not exist on this branch. The two must be reconciled — the measure identity,
 * the PAS references and the insulation flag should end up in one place, with
 * this file consuming them rather than declaring them.
 */
export const PAS_MEASURES: readonly MeasureDefinition[] = [
  defineMeasure(
    "cavity-wall-insulation",
    "Cavity wall insulation, including party walls",
    "BFM.1",
    "B.1",
    true,
    "B1-l2 2)",
    CAVITY_WALL_CHECKS,
  ),
  defineMeasure(
    "external-wall-insulation",
    "External wall insulation",
    "BFM.4",
    "B.4",
    true,
    "B4-l2 3)",
    EXTERNAL_WALL_CHECKS,
  ),
  defineMeasure(
    "internal-wall-insulation",
    "Internal wall insulation",
    "BFM.8",
    "B.8",
    true,
    "B8-l2 6)",
    INTERNAL_WALL_CHECKS,
  ),
  defineMeasure(
    "loft-insulation",
    "Loft insulation",
    "BFM.9",
    "B.9",
    true,
    "B9-l2 2)",
    ROOF_SPACE_CONDITION_CHECKS,
  ),
  defineMeasure(
    "room-in-roof-insulation",
    "Room-in-roof insulation",
    "BFM.12",
    "B.12",
    true,
    "B12-l2 2)",
    ROOM_IN_ROOF_CHECKS,
  ),
  defineMeasure(
    "boiler-replacement",
    "Condensing boiler replacement, natural gas or LPG fired",
    "BSM.1",
    "C.1",
    false,
    null,
    BOILER_CHECKS,
  ),
  defineMeasure(
    "heating-controls",
    "Heating and hot water system controls",
    "BSM.5.1",
    "C.5",
    false,
    null,
    HEATING_CONTROLS_CHECKS,
  ),
];

export function measureDefinition(id: PasMeasure): MeasureDefinition {
  const found = PAS_MEASURES.find((m) => m.id === id);
  if (!found) {
    // Unreachable through the type, but reachable from JSON off the wire, and a
    // silently empty checklist would be the worst possible failure here.
    throw new Error(`No PAS 2030 measure definition for "${String(id)}".`);
  }
  return found;
}

/** The full checklist for a measure, in the order it is inspected and printed. */
export function checksForMeasure(id: PasMeasure): readonly InspectionCheck[] {
  return measureDefinition(id).checks;
}

// ---------------------------------------------------------------------------
// Input
// ---------------------------------------------------------------------------

export type CheckOutcome = "satisfactory" | "not-satisfactory" | "not-applicable";

export interface CheckResponse {
  readonly checkId: string;
  readonly outcome: CheckOutcome;
  readonly note?: string;
  readonly photographReferences?: readonly string[];
}

export interface PhotographReference {
  /** How the photograph is identified in the job's evidence store. */
  readonly reference: string;
  readonly description: string;
  readonly takenOn?: string;
}

export type DefectSeverity = "minor" | "significant" | "severe";

export interface PreExistingDefect {
  readonly location: string;
  readonly description: string;
  readonly severity: DefectSeverity;
  readonly photographReferences?: readonly string[];
  readonly reportedToCoordinator?: boolean;
}

/**
 * The pre-existing condition record.
 *
 * This is the section that decides who pays. PAS 2030:2023 §7.4.4 makes the
 * installer responsible for reporting defects found during the works that the
 * assessment report did not identify — so without a dated baseline taken before
 * anybody set foot inside, an installer cannot show that a crack, a stain or a
 * damp patch pre-dated them, and in practice they own it. B9-l2 makes the
 * baseline explicit for loft insulation; we take it on every measure.
 */
export interface PreExistingConditionRecord {
  /** Explicit: an absent survey and a survey that found nothing are not the same. */
  readonly surveyed: boolean;
  readonly defects: readonly PreExistingDefect[];
  /** `null` means not yet assessed. */
  readonly dampOrMouldObserved: boolean | null;
  readonly dampOrMouldDetail?: string;
  readonly notes?: string;
}

export type VentilationSystemType =
  | "none"
  | "openable-windows-only"
  | "background-ventilators"
  | "intermittent-extract"
  | "continuous-mev"
  | "mvhr"
  | "single-room-hrv"
  | "positive-input"
  | "other";

export interface VentilationProvision {
  readonly location: string;
  readonly type: VentilationSystemType;
  readonly makeAndModel?: string;
  /** Measured flow rate in litres per second, `null` where not measured. */
  readonly measuredFlowLitresPerSecond?: number | null;
  /** Required rate for the room under the relevant nation's standard. */
  readonly requiredFlowLitresPerSecond?: number | null;
  readonly functioning?: boolean | null;
  readonly notes?: string;
}

/**
 * Ventilation and condensation risk.
 *
 * The trigger is transcribed from PAS 2035:2023 §8.2.13 and Annex C.2.1: the
 * adequacy of existing ventilation must be confirmed where the package of
 * specified and existing measures falls into Approved Document F category B or
 * C, or where the retrofit assessment found evidence of condensation or mould.
 * Categories come from Approved Document F Volume 1 Table 3.1 (which measures
 * count as major and minor) and Diagram 3.1 (the resulting category).
 */
export interface VentilationAssessment {
  /** Major measures counted per Approved Document F Table 3.1. `null` = not counted. */
  readonly majorMeasureCount: number | null;
  /** Minor measures counted per Approved Document F Table 3.1. */
  readonly minorMeasureCount: number | null;
  /** Category read from Approved Document F Diagram 3.1. */
  readonly adfCategory: "A" | "B" | "C" | null;
  /** Evidence of condensation or mould growth anywhere in the dwelling. */
  readonly evidenceOfCondensationOrMould: boolean | null;
  readonly evidenceDetail?: string;
  /** What is currently there, room by room. An empty list is not an answer. */
  readonly existingProvisions: readonly VentilationProvision[];
  /** PAS 2035:2023 §7.3.2 f) 3) — fans functionally tested, not just observed. */
  readonly functionalTestingCarriedOut: boolean | null;
  /** Does the retrofit design specify a ventilation upgrade? */
  readonly designSpecifiesUpgrade: boolean | null;
  /** Where in the design the upgrade is specified. */
  readonly designProvisionReference?: string;
  readonly assessedBy?: string;
  readonly notes?: string;
}

export interface InspectorRecord {
  readonly name: string;
  /** The vocational qualification relied on, named as the certificate names it. */
  readonly qualification: string;
  readonly awardingBody?: string;
  readonly certificateNumber?: string;
  /**
   * When the Retrofit Installer last verified this competence. PAS 2030:2023
   * B*-l5 requires verification at intervals no greater than twelve months.
   */
  readonly competenceVerifiedOn?: string;
  readonly employer?: string;
}

export type IndependentCheckRoute =
  | "independent-check"
  | "qa-scheme-pre-notification";

export interface IndependentCheckRecord {
  readonly route: IndependentCheckRoute;
  readonly checkedBy?: string;
  readonly checkedOn?: string;
  readonly organisation?: string;
  /** The randomly selected 1 in 10 that includes physical inspection. */
  readonly physicalInspectionOfDwelling?: boolean;
  readonly independentOfInspectorAndInstaller?: boolean;
  readonly notPaidByResults?: boolean;
  readonly schemeName?: string;
  readonly preNotificationReference?: string;
}

export interface InspectionFinding {
  readonly reference: string;
  /** The checklist item this came from, where it came from one. */
  readonly checkId?: string;
  readonly description: string;
  readonly action: string;
  /** True where installation must not start until this is resolved. */
  readonly blocksInstallation: boolean;
  readonly referredToCoordinatorOn?: string;
  readonly resolution?: string;
  readonly resolvedOn?: string;
}

export interface PasInspectionInput {
  readonly header: DocumentHeader;
  readonly measure: PasMeasure;
  /** Unique identifier for this inspection — PAS 2030:2023 B*-l2 requires one. */
  readonly inspectionReference: string;
  readonly inspectedOn: string;

  readonly project: {
    readonly retrofitCoordinator: string;
    readonly retrofitDesignReference: string;
    readonly retrofitDesignVersion?: string;
    readonly methodStatementReference: string;
    readonly assessmentReportReference?: string;
    readonly trustmarkProjectReference?: string;
    readonly mainContractor?: string;
  };

  readonly inspector: InspectorRecord;
  readonly independentCheck?: IndependentCheckRecord;

  readonly property?: {
    readonly dwellingType?: string;
    readonly construction?: string;
    readonly approximateAge?: string;
    readonly occupiedDuringWorks?: boolean;
  };

  readonly preExistingCondition: PreExistingConditionRecord;
  readonly checks: readonly CheckResponse[];
  /** Required for every insulation measure. Ignored for the others. */
  readonly ventilation?: VentilationAssessment;

  readonly protectedSpecies: {
    /** `null` means not yet looked for. */
    readonly observed: boolean | null;
    readonly detail?: string;
    readonly reportedToCoordinator?: boolean;
  };

  readonly clientConfirmation: {
    readonly scopeUnderstood: boolean | null;
    readonly accessAndStorageAdequate: boolean | null;
    readonly workingHoursAgreed: boolean | null;
    readonly clientName?: string;
    readonly confirmedOn?: string;
  };

  readonly findings: readonly InspectionFinding[];
  readonly photographs: readonly PhotographReference[];

  readonly outcome: {
    /** `null` means undecided; `false` is a legitimate, issuable conclusion. */
    readonly installationMayProceed: boolean | null;
    readonly conditions?: string;
    readonly coordinatorNotifiedOn?: string;
    readonly coordinatorWrittenAgreementReference?: string;
  };

  readonly inspectorComments?: string;
  readonly signature?: {
    readonly signatoryName?: string;
    readonly signedOn?: string;
  };
}

// ---------------------------------------------------------------------------
// Ventilation verdict
// ---------------------------------------------------------------------------

export type VentilationVerdict = "required" | "not-required" | "undetermined";

export interface VentilationConclusion {
  readonly verdict: VentilationVerdict;
  /** Plain-English reasons, printed on the page. */
  readonly reasons: readonly string[];
}

/**
 * Whether the assessment shows a ventilation intervention is needed.
 *
 * Transcribed from PAS 2035:2023 §8.2.13 / Annex C.2.1: category B or C, or
 * evidence of condensation or mould. `undetermined` where either input is
 * missing — which is a blocking state, not a pass, because "we never looked" is
 * how a property ends up mouldy and the installer ends up in front of a panel.
 */
export function ventilationConclusion(
  assessment: VentilationAssessment | undefined,
): VentilationConclusion {
  if (!assessment) {
    return {
      verdict: "undetermined",
      reasons: ["No ventilation assessment has been recorded."],
    };
  }

  const reasons: string[] = [];
  const { adfCategory, evidenceOfCondensationOrMould } = assessment;

  if (adfCategory === null && evidenceOfCondensationOrMould === null) {
    return {
      verdict: "undetermined",
      reasons: [
        "The Approved Document F category has not been determined and evidence of condensation or mould has not been assessed.",
      ],
    };
  }

  if (adfCategory === "B" || adfCategory === "C") {
    reasons.push(
      `The package of specified and existing measures falls into Approved Document F category ${adfCategory}.`,
    );
  }
  if (evidenceOfCondensationOrMould === true) {
    reasons.push(
      "There is evidence of condensation or mould growth in the dwelling.",
    );
  }
  if (reasons.length > 0) {
    return { verdict: "required", reasons };
  }

  // Neither trigger fired, but a null on either input means we cannot say so.
  if (adfCategory === null) {
    return {
      verdict: "undetermined",
      reasons: ["The Approved Document F category has not been determined."],
    };
  }
  if (evidenceOfCondensationOrMould === null) {
    return {
      verdict: "undetermined",
      reasons: [
        "Whether there is evidence of condensation or mould has not been assessed.",
      ],
    };
  }

  return {
    verdict: "not-required",
    reasons: [
      "The measures fall into Approved Document F category A and no condensation or mould was found, so PAS 2035:2023 §8.2.13 does not require the existing ventilation to be upgraded.",
    ],
  };
}

// ---------------------------------------------------------------------------
// Completeness
// ---------------------------------------------------------------------------

function responseIndex(
  responses: readonly CheckResponse[],
): ReadonlyMap<string, CheckResponse> {
  const map = new Map<string, CheckResponse>();
  for (const response of responses) map.set(response.checkId, response);
  return map;
}

/**
 * What still has to be recorded before this may be ISSUED.
 *
 * Rendering an incomplete record is fine and expected — this list is what the
 * portal shows the inspector while they work, and what the issue gate refuses
 * on. Ordered so the blocking, hard-to-fix-later items come first.
 */
export function missingPasInspectionItems(
  input: PasInspectionInput,
): readonly string[] {
  const missing: string[] = [];
  const definition = measureDefinition(input.measure);
  const responses = responseIndex(input.checks ?? []);

  // --- Identity of the inspection --------------------------------------------
  if (!input.inspectionReference?.trim()) {
    missing.push(
      "a unique identifier for this inspection (PAS 2030:2023 requires one so the independent check can be traced to it)",
    );
  }
  if (!input.inspectedOn?.trim()) {
    missing.push("the date the inspection was carried out");
  }
  if (!input.inspector?.name?.trim()) {
    missing.push("the name of the person who carried out the inspection");
  }
  if (!input.inspector?.qualification?.trim()) {
    missing.push(
      "the inspector's vocational qualification (PAS 2030:2023 §7.2.1 requires a vocationally competent person as defined in the measure annex)",
    );
  }
  if (!input.project?.retrofitDesignReference?.trim()) {
    missing.push(
      "the retrofit design reference the inspection was carried out against",
    );
  }
  if (!input.project?.methodStatementReference?.trim()) {
    missing.push(
      "the location-specific installation method statement reference (PAS 2030:2023 §7.2.3)",
    );
  }

  // --- The checklist itself ---------------------------------------------------
  const unanswered = definition.checks.filter((c) => !responses.has(c.id));
  if (unanswered.length > 0) {
    missing.push(
      `${unanswered.length} checklist item${unanswered.length === 1 ? "" : "s"} not yet checked: ${unanswered
        .map((c) => c.id)
        .join(", ")}`,
    );
  }
  for (const check of definition.checks) {
    const response = responses.get(check.id);
    if (!response) continue;
    if (response.outcome === "not-satisfactory" && !response.note?.trim()) {
      missing.push(`an explanation of the failure recorded against ${check.id}`);
    }
    // "Not applicable" is the answer an assessor probes hardest, so it has to
    // carry its reason on the page rather than in somebody's memory.
    if (response.outcome === "not-applicable" && !response.note?.trim()) {
      missing.push(
        `the reason ${check.id} is not applicable at this property (our requirement, not PAS 2030)`,
      );
    }
  }

  // --- Pre-existing condition -------------------------------------------------
  if (!input.preExistingCondition?.surveyed) {
    missing.push(
      "the pre-existing condition survey — an installer who does not record existing damage owns it afterwards",
    );
  }
  if (input.preExistingCondition?.dampOrMouldObserved === null) {
    missing.push(
      "whether pre-existing damp or mould was observed at the property",
    );
  }
  if (
    input.preExistingCondition?.dampOrMouldObserved === true &&
    !input.preExistingCondition.dampOrMouldDetail?.trim()
  ) {
    missing.push("the location and extent of the pre-existing damp or mould");
  }

  // --- Ventilation, for every insulation measure ------------------------------
  if (definition.isInsulation) {
    missing.push(...missingVentilationItems(input));
  }

  // --- Protected species ------------------------------------------------------
  if (input.protectedSpecies?.observed === null) {
    missing.push(
      "whether protected species or plants are present at the location (PAS 2030:2023 §7.2.7)",
    );
  }
  if (
    input.protectedSpecies?.observed === true &&
    input.protectedSpecies.reportedToCoordinator !== true
  ) {
    missing.push(
      "confirmation the protected species finding was made known to the Retrofit Installer and Retrofit Coordinator (PAS 2030:2023 §7.2.7)",
    );
  }

  // --- Client confirmation ----------------------------------------------------
  const client = input.clientConfirmation;
  if (client?.scopeUnderstood !== true) {
    missing.push(
      "the client's confirmation that the nature and extent of the installation matches their expectations (PAS 2030:2023 §7.2.5)",
    );
  }
  if (client?.accessAndStorageAdequate !== true) {
    missing.push(
      "the client's confirmation that site access and materials storage arrangements are adequate (PAS 2030:2023 §7.2.5)",
    );
  }
  if (client?.workingHoursAgreed !== true) {
    missing.push(
      "the client's confirmation that the intended days and hours of working are acceptable (PAS 2030:2023 §7.2.5)",
    );
  }

  // --- Independent check of the inspection ------------------------------------
  if (definition.independentCheckClause) {
    missing.push(...missingIndependentCheckItems(input, definition));
  }

  // --- Findings and outcome ---------------------------------------------------
  for (const finding of input.findings ?? []) {
    if (finding.blocksInstallation && !finding.referredToCoordinatorOn) {
      missing.push(
        `referral of finding ${finding.reference} to the Retrofit Coordinator (PAS 2030:2023 §7.2.8)`,
      );
    }
  }
  if (input.outcome?.installationMayProceed === null) {
    missing.push("the inspector's conclusion on whether installation may proceed");
  }
  if (input.outcome?.installationMayProceed === true) {
    // A record that says "go ahead" while a blocking finding is open contradicts
    // §7.2.8. A record that says "do not proceed" with findings open does not —
    // that is exactly what such a record is for — so it is issuable.
    for (const finding of input.findings ?? []) {
      if (finding.blocksInstallation && !finding.resolvedOn) {
        missing.push(
          `an agreed solution for finding ${finding.reference}; installation may not commence until the Retrofit Coordinator has agreed one (PAS 2030:2023 §7.2.8)`,
        );
      }
    }
    if ((input.findings ?? []).some((f) => f.blocksInstallation)) {
      if (!input.outcome.coordinatorWrittenAgreementReference?.trim()) {
        missing.push(
          "the Retrofit Coordinator's written agreement to proceed (PAS 2030:2023 §7.2.8)",
        );
      }
    }
  }

  // --- Evidence and signature -------------------------------------------------
  if (definition.isInsulation && (input.photographs ?? []).length === 0) {
    missing.push(
      "at least one photograph of the inspected areas (our requirement, not PAS 2030)",
    );
  }
  if (!input.signature?.signedOn) {
    missing.push("the inspector's signature and the date it was signed");
  }
  if (!input.signature?.signatoryName?.trim()) {
    missing.push("the name of the person signing this record");
  }

  return missing;
}

function missingVentilationItems(input: PasInspectionInput): readonly string[] {
  const missing: string[] = [];
  const ventilation = input.ventilation;

  if (!ventilation) {
    missing.push(
      "the ventilation and condensation risk assessment, which PAS 2035:2023 §8.2.13 requires whenever insulation is added",
    );
    return missing;
  }

  if (ventilation.majorMeasureCount === null || ventilation.minorMeasureCount === null) {
    missing.push(
      "the count of major and minor measures under Approved Document F Table 3.1, including measures already fitted",
    );
  }
  if (ventilation.adfCategory === null) {
    missing.push(
      "the Approved Document F category (Diagram 3.1) for the package of specified and existing measures",
    );
  }
  if (ventilation.evidenceOfCondensationOrMould === null) {
    missing.push(
      "whether there is evidence of condensation or mould growth in the dwelling",
    );
  }
  if (ventilation.existingProvisions.length === 0) {
    missing.push(
      "the record of existing ventilation provision room by room — record 'none' explicitly where there is none (PAS 2035:2023 §7.3.2 f))",
    );
  }
  if (ventilation.functionalTestingCarriedOut === null) {
    missing.push(
      "whether the existing ventilation was functionally tested rather than only observed (PAS 2035:2023 §7.3.2 f) 3))",
    );
  }

  const conclusion = ventilationConclusion(ventilation);
  if (conclusion.verdict === "required" && input.outcome?.installationMayProceed !== false) {
    // Where the record concludes installation must not proceed, the upgrade
    // route is the next project's problem; the assessment itself still stands.
    if (ventilation.designSpecifiesUpgrade === null) {
      missing.push(
        "whether the retrofit design specifies the ventilation upgrade this assessment shows is needed (PAS 2030:2023 §5.2.4)",
      );
    } else if (ventilation.designSpecifiesUpgrade === true) {
      if (!ventilation.designProvisionReference?.trim()) {
        missing.push(
          "where in the retrofit design the ventilation upgrade is specified",
        );
      }
    } else {
      // Matched on the check id first. Matching only on the wording of a
      // free-text description would let a compliance gate pass or fail on how
      // somebody phrased a note, which is not a gate at all.
      const referred = (input.findings ?? []).some(
        (f) =>
          f.referredToCoordinatorOn &&
          (f.checkId === VENTILATION_DESIGN_CHECK_ID ||
            /ventilat/i.test(f.description)),
      );
      if (!referred) {
        missing.push(
          "referral to the Retrofit Coordinator of the missing ventilation provision; a ventilation intervention is required and the design does not specify one (PAS 2030:2023 §5.2.4)",
        );
      }
    }
  }

  return missing;
}

function missingIndependentCheckItems(
  input: PasInspectionInput,
  definition: MeasureDefinition,
): readonly string[] {
  const clause = definition.independentCheckClause ?? "";
  const check = input.independentCheck;
  if (!check) {
    return [
      `the independent check of this inspection, or a recognised industry quality assurance pre-notification instead (PAS 2030:2023 ${clause})`,
    ];
  }

  const missing: string[] = [];
  if (check.route === "independent-check") {
    if (!check.checkedBy?.trim()) {
      missing.push("the name of the person who independently checked this inspection");
    }
    if (!check.checkedOn?.trim()) {
      missing.push("the date this inspection was independently checked");
    }
    if (check.independentOfInspectorAndInstaller !== true) {
      missing.push(
        `confirmation the checker is independent of the inspector and of the installer (PAS 2030:2023 ${clause})`,
      );
    }
    if (check.notPaidByResults !== true) {
      missing.push(
        `confirmation the checker is not paid by results nor remunerated only where an assessment is found valid (PAS 2030:2023 ${clause})`,
      );
    }
  } else {
    if (!check.schemeName?.trim()) {
      missing.push(
        "the name of the industry quality assurance scheme the pre-notification was made under",
      );
    }
    if (!check.preNotificationReference?.trim()) {
      missing.push("the pre-notification reference from the quality assurance scheme");
    }
  }
  return missing;
}

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

const NOT_RECORDED = '<i class="pending">not recorded</i>';
const NOT_CHECKED = '<i class="pending">not yet checked</i>';

function yesNo(value: boolean | null | undefined): string {
  if (value === true) return "Yes";
  if (value === false) return "<b>No</b>";
  return NOT_RECORDED;
}

function outcomeLabel(response: CheckResponse | undefined): string {
  if (!response) return NOT_CHECKED;
  switch (response.outcome) {
    case "satisfactory":
      return "Satisfactory";
    case "not-satisfactory":
      return "<b>Not satisfactory</b>";
    case "not-applicable":
      return "Not applicable";
  }
}

function basisCell(basis: CheckBasis): string {
  if (basis.kind === "transcribed") {
    return `${esc(basis.standard)} ${esc(basis.clause)}${
      basis.guidanceOnly ? " <i>(guidance)</i>" : ""
    }`;
  }
  return `<b>Our practice</b> — ${esc(basis.why)}`;
}

function flow(value: number | null | undefined): string {
  if (value === null || value === undefined) return NOT_RECORDED;
  return `${num(value, 1)} l/s`;
}

const VENTILATION_TYPE_LABELS: Readonly<Record<VentilationSystemType, string>> = {
  none: "None",
  "openable-windows-only": "Openable windows only",
  "background-ventilators": "Background ventilators (trickle)",
  "intermittent-extract": "Intermittent extract fan",
  "continuous-mev": "Continuous mechanical extract (MEV)",
  mvhr: "Mechanical ventilation with heat recovery (MVHR)",
  "single-room-hrv": "Single-room heat recovery ventilator",
  "positive-input": "Positive input ventilation (PIV)",
  other: "Other",
};

const SEVERITY_LABELS: Readonly<Record<DefectSeverity, string>> = {
  minor: "Minor",
  significant: "Significant",
  severe: "<b>Severe</b>",
};

function renderChecklist(
  definition: MeasureDefinition,
  responses: ReadonlyMap<string, CheckResponse>,
): string {
  // Groups printed in first-appearance order, which is the order the checks are
  // defined in — stable, and therefore the same every render.
  const groups: string[] = [];
  for (const check of definition.checks) {
    if (!groups.includes(check.group)) groups.push(check.group);
  }

  return groups
    .map((group) => {
      const rows = definition.checks.filter((c) => c.group === group);
      return `<h2>${esc(group)}</h2>
${table<InspectionCheck>(
  [
    { heading: "Item", cell: (c) => esc(c.prompt) },
    { heading: "Basis", cell: (c) => basisCell(c.basis) },
    { heading: "Outcome", cell: (c) => outcomeLabel(responses.get(c.id)) },
    {
      heading: "Notes and photographs",
      cell: (c) => {
        const response = responses.get(c.id);
        if (!response) return "—";
        const parts: string[] = [];
        if (response.note?.trim()) parts.push(esc(response.note));
        const photos = response.photographReferences ?? [];
        if (photos.length > 0) {
          parts.push(`Photographs: ${photos.map((p) => esc(p)).join(", ")}`);
        }
        return parts.length > 0 ? parts.join("<br>") : "—";
      },
    },
  ],
  rows,
)}`;
    })
    .join("\n");
}

function renderVentilation(input: PasInspectionInput): string {
  const ventilation = input.ventilation;
  const conclusion = ventilationConclusion(ventilation);

  const banner =
    conclusion.verdict === "required"
      ? `<div class="fail"><b>A ventilation intervention is required at this property.</b><ul>${conclusion.reasons
          .map((r) => `<li>${esc(r)}</li>`)
          .join(
            "",
          )}</ul>PAS 2035:2023 §8.2.13 requires the adequacy of the existing ventilation to be confirmed and, where it is not adequate, a specification for upgrading it to be included in the retrofit design in accordance with Annex C. Insulating this property without doing so raises the risk of condensation and mould growth.</div>`
      : conclusion.verdict === "undetermined"
        ? `<div class="fail"><b>The ventilation and condensation risk assessment is not complete.</b><ul>${conclusion.reasons
            .map((r) => `<li>${esc(r)}</li>`)
            .join(
              "",
            )}</ul>This inspection cannot be issued for an insulation measure until it is.</div>`
        : `<div class="warn"><b>No ventilation intervention is required by PAS 2035:2023 §8.2.13 on the basis recorded below.</b> ${esc(
            conclusion.reasons[0] ?? "",
          )} The client should still be advised on managing condensation, damp and mould (PAS 2035:2023 Annex C.3.1).</div>`;

  const summary = fieldList([
    [
      "Major measures (Approved Document F Table 3.1)",
      ventilation?.majorMeasureCount === null ||
      ventilation?.majorMeasureCount === undefined
        ? NOT_RECORDED
        : num(ventilation.majorMeasureCount, 0),
    ],
    [
      "Minor measures (Approved Document F Table 3.1)",
      ventilation?.minorMeasureCount === null ||
      ventilation?.minorMeasureCount === undefined
        ? NOT_RECORDED
        : num(ventilation.minorMeasureCount, 0),
    ],
    [
      "Category (Approved Document F Diagram 3.1)",
      ventilation?.adfCategory
        ? `<b>Category ${esc(ventilation.adfCategory)}</b>`
        : NOT_RECORDED,
    ],
    [
      "Evidence of condensation or mould",
      ventilation?.evidenceOfCondensationOrMould === null ||
      ventilation?.evidenceOfCondensationOrMould === undefined
        ? NOT_RECORDED
        : ventilation.evidenceOfCondensationOrMould
          ? `<b>Yes</b>${
              ventilation.evidenceDetail
                ? ` — ${esc(ventilation.evidenceDetail)}`
                : ""
            }`
          : "None observed",
    ],
    [
      "Existing ventilation functionally tested",
      yesNo(ventilation?.functionalTestingCarriedOut),
    ],
    [
      "Retrofit design specifies a ventilation upgrade",
      yesNo(ventilation?.designSpecifiesUpgrade),
    ],
    [
      "Where specified in the design",
      ventilation?.designProvisionReference
        ? esc(ventilation.designProvisionReference)
        : NOT_RECORDED,
    ],
    [
      "Assessed by",
      ventilation?.assessedBy ? esc(ventilation.assessedBy) : NOT_RECORDED,
    ],
  ]);

  const provisions =
    ventilation && ventilation.existingProvisions.length > 0
      ? table<VentilationProvision>(
          [
            { heading: "Location", cell: (p) => esc(p.location) },
            {
              heading: "Provision",
              cell: (p) => esc(VENTILATION_TYPE_LABELS[p.type]),
            },
            { heading: "Make and model", cell: (p) => esc(p.makeAndModel ?? "—") },
            {
              heading: "Measured",
              numeric: true,
              cell: (p) => flow(p.measuredFlowLitresPerSecond),
            },
            {
              heading: "Required",
              numeric: true,
              cell: (p) => flow(p.requiredFlowLitresPerSecond),
            },
            { heading: "Working", cell: (p) => yesNo(p.functioning) },
          ],
          ventilation.existingProvisions,
          { caption: "Existing ventilation provision, as found" },
        )
      : `<p class="pending">Existing ventilation provision has not been recorded. Record it room by room, and record "none" explicitly where there is none.</p>`;

  const notes = ventilation?.notes
    ? `<p>${esc(ventilation.notes)}</p>`
    : "";

  return `<h2>Ventilation and condensation risk</h2>
<p class="standard">Mandatory for every insulation measure. Trigger from PAS 2035:2023 §8.2.13 and Annex C.2.1; categories from Approved Document F Volume 1 Table 3.1 and Diagram 3.1. Adding insulation reduces infiltration, and inadequate ventilation afterwards is the most common cause of retrofit failure and complaint.</p>
${banner}
${summary}
${provisions}
${notes}`;
}

function renderPreExistingCondition(input: PasInspectionInput): string {
  const record = input.preExistingCondition;

  const header = fieldList([
    ["Condition survey carried out", yesNo(record?.surveyed ?? null)],
    [
      "Pre-existing damp or mould",
      record?.dampOrMouldObserved === null || record?.dampOrMouldObserved === undefined
        ? NOT_RECORDED
        : record.dampOrMouldObserved
          ? `<b>Yes</b>${
              record.dampOrMouldDetail ? ` — ${esc(record.dampOrMouldDetail)}` : ""
            }`
          : "None observed",
    ],
  ]);

  const defects =
    (record?.defects ?? []).length > 0
      ? table<PreExistingDefect>(
          [
            { heading: "Location", cell: (d) => esc(d.location) },
            { heading: "Defect", cell: (d) => esc(d.description) },
            { heading: "Severity", cell: (d) => SEVERITY_LABELS[d.severity] },
            {
              heading: "Photographs",
              cell: (d) =>
                (d.photographReferences ?? []).length > 0
                  ? (d.photographReferences ?? []).map((p) => esc(p)).join(", ")
                  : `<i class="pending">none</i>`,
            },
            {
              heading: "Reported to coordinator",
              cell: (d) => yesNo(d.reportedToCoordinator ?? null),
            },
          ],
          record?.defects ?? [],
          { caption: "Pre-existing defects, recorded before any work began" },
        )
      : record?.surveyed
        ? `<p>No pre-existing defects were observed at this inspection.</p>`
        : `<p class="pending">No condition survey has been recorded.</p>`;

  return `<h2>Pre-existing condition of the property</h2>
<p class="standard">Recorded before installation begins. PAS 2030:2023 §7.4.4 makes the installer responsible for reporting defects found during the works that the assessment did not identify; without this dated baseline, damage found later is difficult to show as pre-existing. B9-l2 requires it explicitly for loft insulation and we take it on every measure.</p>
${header}
${defects}
${record?.notes ? `<p>${esc(record.notes)}</p>` : ""}`;
}

function renderFindings(input: PasInspectionInput): string {
  const findings = input.findings ?? [];
  const outcome = input.outcome;

  const body =
    findings.length > 0
      ? table<InspectionFinding>(
          [
            { heading: "Ref", cell: (f) => esc(f.reference) },
            {
              heading: "Finding",
              cell: (f) =>
                `${esc(f.description)}${
                  f.checkId ? `<br><i>${esc(f.checkId)}</i>` : ""
                }`,
            },
            { heading: "Action", cell: (f) => esc(f.action) },
            {
              heading: "Blocks installation",
              cell: (f) => (f.blocksInstallation ? "<b>Yes</b>" : "No"),
            },
            {
              heading: "Referred to coordinator",
              cell: (f) =>
                f.referredToCoordinatorOn
                  ? esc(isoDateToLong(f.referredToCoordinatorOn))
                  : NOT_RECORDED,
            },
            {
              heading: "Resolution",
              cell: (f) =>
                f.resolvedOn
                  ? `${esc(f.resolution ?? "")} — ${esc(isoDateToLong(f.resolvedOn))}`
                  : `<i class="pending">open</i>`,
            },
          ],
          findings,
        )
      : `<p>No findings were raised by this inspection.</p>`;

  const unresolvedBlocking = findings.filter(
    (f) => f.blocksInstallation && !f.resolvedOn,
  );

  const proceedNotice =
    outcome?.installationMayProceed === false
      ? `<div class="fail"><b>Installation must not commence.</b> ${esc(
          outcome.conditions ?? "See the findings above.",
        )}</div>`
      : outcome?.installationMayProceed === true && unresolvedBlocking.length > 0
        ? `<div class="fail"><b>This record says installation may proceed while ${unresolvedBlocking.length} blocking finding${
            unresolvedBlocking.length === 1 ? " remains" : "s remain"
          } open.</b> PAS 2030:2023 §7.2.8 requires a mutually agreed solution with the Retrofit Coordinator, confirmed with the client, before installation commences.</div>`
        : outcome?.installationMayProceed === true
          ? `<div class="warn">Installation may proceed${
              outcome.conditions ? `, subject to: ${esc(outcome.conditions)}` : "."
            }</div>`
          : `<div class="warn">The inspector has not yet concluded whether installation may proceed.</div>`;

  return `<h2>Findings and action taken</h2>
<p class="standard">PAS 2030:2023 §7.2.8 — where the inspection reveals potential installation problems the Retrofit Coordinator and any relevant statutory authority must be notified, and installation must not commence until an agreed solution is confirmed with the client.</p>
${body}
${proceedNotice}
${fieldList([
  [
    "Retrofit Coordinator notified",
    outcome?.coordinatorNotifiedOn
      ? esc(isoDateToLong(outcome.coordinatorNotifiedOn))
      : NOT_RECORDED,
  ],
  [
    "Coordinator's written agreement",
    outcome?.coordinatorWrittenAgreementReference
      ? esc(outcome.coordinatorWrittenAgreementReference)
      : NOT_RECORDED,
  ],
])}`;
}

export const pasInspectionTemplate: DocumentTemplate<PasInspectionInput> = {
  type: "pas-inspection",
  version: "pas-inspection@1.0.0",
  standardsRef:
    "Pre-installation building inspection, PAS 2030:2023 §7.2 and the measure-specific annex",

  render(input: PasInspectionInput): RenderedDocument {
    const { header } = input;
    const definition = measureDefinition(input.measure);
    const responses = responseIndex(input.checks ?? []);
    const outstanding = missingPasInspectionItems(input);

    const sections: string[] = [];

    sections.push(
      `<p class="standard">Carried out before installation begins, as required by PAS 2030:2023 §7.2. This record and its findings are retained by the Retrofit Installer and made available to the Retrofit Coordinator or a validation body on request (§7.2.6).</p>`,
    );

    if (outstanding.length > 0) {
      sections.push(
        `<div class="fail"><b>Not yet complete — ${outstanding.length} item${
          outstanding.length === 1 ? "" : "s"
        } outstanding</b><ul>${outstanding
          .map((m) => `<li>${esc(m)}</li>`)
          .join("")}</ul>This record may not be issued until they are recorded.</div>`,
      );
    }

    sections.push(`<h2>Measure and project</h2>
${fieldList([
  [
    "Measure",
    `<b>${esc(definition.name)}</b> — ${esc(definition.measureReference)}, PAS 2030:2023 Annex ${esc(definition.annex)}`,
  ],
  ["Inspection reference", esc(input.inspectionReference || "") || NOT_RECORDED],
  [
    "Date of inspection",
    input.inspectedOn ? esc(isoDateToLong(input.inspectedOn)) : NOT_RECORDED,
  ],
  ["Retrofit Coordinator", esc(input.project?.retrofitCoordinator ?? "") || NOT_RECORDED],
  [
    "Retrofit design",
    `${esc(input.project?.retrofitDesignReference ?? "") || NOT_RECORDED}${
      input.project?.retrofitDesignVersion
        ? ` (version ${esc(input.project.retrofitDesignVersion)})`
        : ""
    }`,
  ],
  [
    "Installation method statement",
    esc(input.project?.methodStatementReference ?? "") || NOT_RECORDED,
  ],
  ...(input.project?.assessmentReportReference
    ? ([
        ["Retrofit assessment report", esc(input.project.assessmentReportReference)],
      ] as const)
    : []),
  ...(input.project?.trustmarkProjectReference
    ? ([
        ["TrustMark project reference", esc(input.project.trustmarkProjectReference)],
      ] as const)
    : []),
  ...(input.project?.mainContractor
    ? ([["Main Contractor", esc(input.project.mainContractor)]] as const)
    : []),
  ...(input.property?.dwellingType
    ? ([["Dwelling type", esc(input.property.dwellingType)]] as const)
    : []),
  ...(input.property?.construction
    ? ([["Construction", esc(input.property.construction)]] as const)
    : []),
  ...(input.property?.approximateAge
    ? ([["Approximate age", esc(input.property.approximateAge)]] as const)
    : []),
  ...(input.property?.occupiedDuringWorks !== undefined
    ? ([
        ["Occupied during the works", yesNo(input.property.occupiedDuringWorks)],
      ] as const)
    : []),
])}`);

    sections.push(`<h2>Inspector and competence</h2>
<p class="standard">PAS 2030:2023 §7.2.1 requires the inspection to be undertaken by a vocationally competent person as defined in the measure-specific annex (${esc(definition.annex)}); competence is verified by the Retrofit Installer at intervals no greater than twelve months.</p>
${fieldList([
  ["Inspector", esc(input.inspector?.name ?? "") || NOT_RECORDED],
  ["Qualification relied on", esc(input.inspector?.qualification ?? "") || NOT_RECORDED],
  [
    "Awarding body",
    input.inspector?.awardingBody ? esc(input.inspector.awardingBody) : NOT_RECORDED,
  ],
  [
    "Certificate number",
    input.inspector?.certificateNumber
      ? esc(input.inspector.certificateNumber)
      : NOT_RECORDED,
  ],
  [
    "Competence last verified",
    input.inspector?.competenceVerifiedOn
      ? esc(isoDateToLong(input.inspector.competenceVerifiedOn))
      : NOT_RECORDED,
  ],
  ...(input.inspector?.employer
    ? ([["Employer", esc(input.inspector.employer)]] as const)
    : []),
])}
${renderIndependentCheck(input, definition)}`);

    sections.push(renderPreExistingCondition(input));

    // Ventilation sits ahead of the checklist deliberately. It is the section
    // most often skipped and the one that most often costs somebody a
    // remediation, so it reads before the long list rather than after it.
    if (definition.isInsulation) {
      sections.push(renderVentilation(input));
    }

    sections.push(renderChecklist(definition, responses));

    if (!definition.isInsulation) {
      sections.push(`<h2>Ventilation and condensation risk</h2>
<p>PAS 2035:2023 §8.2.13 ties the ventilation assessment to measures that reduce background infiltration. ${esc(
        definition.name,
      )} is not such a measure, so no assessment is required on this record. Where this measure is installed alongside insulation, the assessment sits on the insulation measure's inspection.</p>`);
    }

    sections.push(`<h2>Protected species</h2>
<p class="standard">PAS 2030:2023 §7.2.7 — bats, birds, butterflies, dormice and protected plants.</p>
${fieldList([
  ["Protected species or plants present", yesNo(input.protectedSpecies?.observed)],
  [
    "Detail",
    input.protectedSpecies?.detail ? esc(input.protectedSpecies.detail) : "—",
  ],
  [
    "Made known to the Retrofit Installer and Coordinator",
    input.protectedSpecies?.observed === true
      ? yesNo(input.protectedSpecies.reportedToCoordinator ?? null)
      : "Not applicable",
  ],
])}`);

    sections.push(`<h2>Client confirmation</h2>
${fieldList([
  [
    "Nature and extent of the installation is understood and expected",
    yesNo(input.clientConfirmation?.scopeUnderstood),
  ],
  [
    "Site access and materials storage adequate",
    yesNo(input.clientConfirmation?.accessAndStorageAdequate),
  ],
  [
    "Intended days and hours of working acceptable",
    yesNo(input.clientConfirmation?.workingHoursAgreed),
  ],
  [
    "Confirmed with",
    input.clientConfirmation?.clientName
      ? `${esc(input.clientConfirmation.clientName)}${
          input.clientConfirmation.confirmedOn
            ? ` on ${esc(isoDateToLong(input.clientConfirmation.confirmedOn))}`
            : ""
        }`
      : NOT_RECORDED,
  ],
])}`);

    sections.push(renderFindings(input));

    sections.push(`<h2>Photographic record</h2>
${
  (input.photographs ?? []).length > 0
    ? table<PhotographReference>(
        [
          { heading: "Reference", cell: (p) => esc(p.reference) },
          { heading: "Description", cell: (p) => esc(p.description) },
          {
            heading: "Taken",
            cell: (p) => (p.takenOn ? esc(isoDateToLong(p.takenOn)) : "—"),
          },
        ],
        input.photographs,
      )
    : `<p class="pending">No photographs have been referenced against this inspection.</p>`
}`);

    const strayResponses = (input.checks ?? []).filter(
      (r) => !definition.checks.some((c) => c.id === r.checkId),
    );
    if (strayResponses.length > 0) {
      // Almost certainly a checklist that changed under a part-completed record.
      // Printing them beats discarding evidence somebody deliberately entered.
      sections.push(`<h2>Responses not on this measure's checklist</h2>
<div class="warn">These answers do not correspond to any item in the ${esc(
        definition.measureReference,
      )} checklist. They are printed so nothing recorded on site is lost, but they do not satisfy any requirement of this inspection.</div>
${table<CheckResponse>(
  [
    { heading: "Item", cell: (r) => esc(r.checkId) },
    { heading: "Outcome", cell: (r) => outcomeLabel(r) },
    { heading: "Note", cell: (r) => esc(r.note ?? "—") },
  ],
  strayResponses,
)}`);
    }

    if (input.inspectorComments) {
      sections.push(
        `<h2>Inspector's comments</h2><p>${esc(input.inspectorComments)}</p>`,
      );
    }

    sections.push(`<h2>Declaration</h2>
<p>I confirm that I carried out this pre-installation building inspection at the property named above, that the findings recorded here are those I observed, and that any potential installation problem has been notified to the Retrofit Coordinator.</p>
${fieldList([
  [
    "Signed",
    input.signature?.signedOn
      ? `${esc(input.signature.signatoryName ?? input.inspector?.name ?? "")} — ${esc(
          isoDateToLong(input.signature.signedOn),
        )}`
      : '<i class="pending">not signed</i>',
  ],
])}`);

    const judgedCount = definition.checks.filter(
      (c) => c.basis.kind === "judged",
    ).length;

    sections.push(`<h2>Provenance of this checklist</h2>
<p class="standard">${esc(PROVENANCE_NOTE)}</p>
<p class="standard">${
      judgedCount === 0
        ? "Every item on this checklist is paraphrased from a clause of PAS 2030:2023."
        : `${num(judgedCount, 0)} of ${num(
            definition.checks.length,
            0,
          )} items on this checklist are our own practice rather than a clause of PAS 2030:2023, and are marked as such in the Basis column.`
    }</p>`);

    const footer = [
      `template ${esc(pasInspectionTemplate.version)}`,
      `measure ${esc(definition.measureReference)}`,
      `issued ${esc(isoDateToLong(header.issuedOn))}`,
      outstanding.length === 0
        ? "complete"
        : `<b>${outstanding.length} item(s) outstanding</b>`,
    ].join(" · ");

    return {
      title: `Pre-installation building inspection — ${definition.name} — ${header.propertyAddress}`,
      templateVersion: pasInspectionTemplate.version,
      html: documentShell({
        title: "PAS 2030 pre-installation building inspection",
        standardsRef: `${pasInspectionTemplate.standardsRef} ${definition.annex} (${definition.measureReference})`,
        header,
        body: sections.join("\n"),
        footer,
      }),
    };
  },
};

function renderIndependentCheck(
  input: PasInspectionInput,
  definition: MeasureDefinition,
): string {
  if (!definition.independentCheckClause) {
    return `<p>PAS 2030:2023 Annex ${esc(
      definition.annex,
    )} does not require this inspection to be independently checked.</p>`;
  }

  const check = input.independentCheck;
  const intro = `<h3>Independent check of this inspection</h3>
<p class="standard">PAS 2030:2023 ${esc(
    definition.independentCheckClause,
  )} — the inspection must be independently checked by an appropriately qualified person before installation starts, with a randomly selected one in ten of those checks including physical inspection of the dwelling, or a recognised industry quality assurance pre-notification undertaken instead.</p>`;

  if (!check) {
    return `${intro}
<div class="fail">No independent check or quality assurance pre-notification has been recorded.</div>`;
  }

  if (check.route === "qa-scheme-pre-notification") {
    return `${intro}
${fieldList([
  ["Route", "Industry quality assurance scheme pre-notification"],
  ["Scheme", check.schemeName ? esc(check.schemeName) : NOT_RECORDED],
  [
    "Pre-notification reference",
    check.preNotificationReference ? esc(check.preNotificationReference) : NOT_RECORDED,
  ],
])}`;
  }

  return `${intro}
${fieldList([
  ["Route", "Independent check by a qualified person"],
  ["Checked by", check.checkedBy ? esc(check.checkedBy) : NOT_RECORDED],
  ["Organisation", check.organisation ? esc(check.organisation) : NOT_RECORDED],
  [
    "Date checked",
    check.checkedOn ? esc(isoDateToLong(check.checkedOn)) : NOT_RECORDED,
  ],
  [
    "Included physical inspection of the dwelling",
    yesNo(check.physicalInspectionOfDwelling ?? null),
  ],
  [
    "Independent of the inspector and the installer",
    yesNo(check.independentOfInspectorAndInstaller ?? null),
  ],
  ["Not paid by results", yesNo(check.notPaidByResults ?? null)],
])}`;
}

/**
 * Guards issuing. The record may be RENDERED at any point during the visit — it
 * is filled in at the property — but it may only be ISSUED once complete.
 */
export function assertPasInspectionComplete(input: PasInspectionInput): void {
  const missing = missingPasInspectionItems(input);
  if (missing.length > 0) {
    throw new IncompleteDocumentError(
      "pre-installation building inspection",
      missing,
    );
  }
}
